import React, { useState ,useEffect,useRef} from 'react';
import { createPortal } from 'react-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Button, FormFeedback } from 'reactstrap';
import { Formik, Form,FieldArray } from 'formik';
import * as Yup from 'yup';
import { LineupData } from '../types';
import ImageCropper from "../../../../components/common/ImageCropper/ImageCropper";
interface LineupModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (lineup: LineupData) => void;
  isLoading?: boolean;
  lineup?: LineupData;
}

const LineupModal: React.FC<LineupModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isLoading = false,
  lineup
}) => {
  const [imageError, setImageError] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  // ✅ cropper states
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);

  useEffect(() => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;

    if (lineup && isOpen) {
       if (lineup.artistImage instanceof File) {
          const url = URL.createObjectURL(lineup.artistImage);
          previewUrlRef.current = url;
          setPreviewImage(url);
       } else {
          setPreviewImage(lineup.artistImageUrl || null);
       }
    } else if (!lineup && isOpen) {
       setPreviewImage(null);
    }
    
    if (isOpen) {
      setIsCropperOpen(false);
      setFileToCrop(null);
      setCroppedFile(null);
      setImageError('');
    }
  }, [lineup, isOpen]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);
  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required('Artist/Performer name is required'),
    // tagLine: Yup.string().required('Tag line is required'),
    // description: Yup.string().required('Description is required'),
  });

  // Initial values
  const initialValues: LineupData = {
    name: lineup?.name || '',
    tagLine: lineup?.tagLine || '',
    description: lineup?.description || '',
    artistImage: lineup?.artistImage || null,
    artistImageKey: lineup?.artistImageKey || '',
    artistImageUrl: lineup?.artistImageUrl || null,
    sortOrder: lineup?.sortOrder || 0,
    isActive: lineup?.isActive ?? true,
     socialMedia: lineup?.socialMedia || [
    { platform: "", url: "" } 
  ] || null,
  };

   const handleSubmit = (values: LineupData) => {
    if (imageError) return; 
    onSubmit({
      ...values,
      id: lineup?.id,
      sortOrder: lineup?.sortOrder || 0,
      isActive: lineup?.isActive ?? true,
    });
  };

  return (
    <Modal modalClassName="quick-v2-glass-modal" isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <i className="fa-solid fa-music me-2"></i>
        {lineup ? 'Edit Lineup Item' : 'Add New Lineup Item'}
      </ModalHeader>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => (
          <Form>
            <ModalBody>
              <div className="row">
                {/* Artist/Performer Name */}
                <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="name" className="form-label">
                      Artist/Performer Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.name && !!errors.name}
                    />
                    <FormFeedback>{errors.name}</FormFeedback>
                  </FormGroup>
                </div>

                {/* Tag Line */}
                <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="tagLine" className="form-label">
                      Tag Line 
                    </Label>
                    <Input
                      id="tagLine"
                      name="tagLine"
                      type="text"
                      value={values.tagLine}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.tagLine && !!errors.tagLine}
                      placeholder="e.g., Grammy Award Winner, Rock Legend, etc."
                    />
                    <FormFeedback>{errors.tagLine}</FormFeedback>
                  </FormGroup>
                </div>

                {/* Description */}
                <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="description" className="form-label">
                      Description 
                    </Label>
                    <Input
                      id="description"
                      name="description"
                      type="textarea"
                      rows={4}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.description && !!errors.description}
                      placeholder="Tell us about this artist/performer..."
                    />
                    <FormFeedback>{errors.description}</FormFeedback>
                  </FormGroup>
                </div>
                {/* Social Media Links */}
                <FieldArray name="socialMedia">
                  {({ push, remove }) => (
                    <div className="col-md-12 mb-3">
                      <Label className="form-label">Social Media Links</Label>
                      {values.socialMedia.map((item, index) => (
                        <div key={index} className="d-flex align-items-start mb-2 gap-2">
                          <Input
                            type="select"
                            name={`socialMedia[${index}].platform`}
                            value={item.platform}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="me-2"
                          >
                            <option value="">Select Platform</option>
                            <option value="facebook">Facebook</option>
                            <option value="instagram">Instagram</option>
                            <option value="twitter">X</option>
                            <option value="youtube">YouTube</option>
                            <option value="spotify">Spotify</option>
                            <option value="apple_music">Apple Music</option>
                          </Input>

                          <Input
                            type="text"
                            name={`socialMedia[${index}].url`}
                            placeholder="https://..."
                            value={item.url}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />

                          <Button
                            color="danger"
                            type="button"
                            onClick={() => remove(index)}
                            disabled={values.socialMedia.length === 1}
                          >
                            <i className="fa fa-trash" />
                          </Button>
                        </div>
                      ))}

                      <Button
                        color="secondary"
                        type="button"
                        onClick={() => {
                          if (values.socialMedia.length < 6) {
                            push({ platform: "", url: "" });
                          }
                        }}
                        disabled={values.socialMedia.length >= 6}
                      >
                        <i className="fa fa-plus me-1" />
                        Add Link
                      </Button>
                    </div>
                  )}
                </FieldArray>

                {/* Artist Image with Cropper */}
                <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="artistImage" className="form-label">
                      Artist Image (Optional)
                    </Label>

                    {previewImage && (
                      <div className="mb-2">
                        <img
                          src={previewImage}
                          alt="Preview"
                          style={{
                            width: 120, height: 120, objectFit: 'cover',
                            border: '1px solid #ddd', borderRadius: 4
                          }}
                        />
                      </div>
                    )}

                    <Input
                      id="artistImage"
                      name="artistImage"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setImageError('');
                        if (!file) return;

                        // 2 MB size check
                        if (file.size > 2 * 1024 * 1024) {
                          setImageError('Max file size is 2 MB');
                          return;
                        }

                        setFileToCrop(file);
                        setIsCropperOpen(true);
                      }}
                      invalid={!!imageError}
                    />
                    {imageError && <FormFeedback>{imageError}</FormFeedback>}
                    <small className="text-muted">
                      Recommended size: 200×200. Max 2 MB.
                    </small>

                    {/* === Cropper Modal === */}
                    {isCropperOpen && fileToCrop && createPortal(
                      <div className="cropper-modal-overlay">
                        <div className="cropper-modal-card">
                          <ImageCropper
                            imageFile={fileToCrop}
                            aspect={1 / 1}
                            onCancel={() => setIsCropperOpen(false)}
                            onCropComplete={(croppedImg) => {
                              setCroppedFile(croppedImg);
                              const url = URL.createObjectURL(croppedImg);
                              if (previewUrlRef.current)
                                URL.revokeObjectURL(previewUrlRef.current);
                              previewUrlRef.current = url;
                              setPreviewImage(url);
                              setFieldValue("artistImage", croppedImg);
                              setFieldValue("artistImageKey", croppedImg.name);
                              setFieldValue("artistImageUrl", url);
                              setIsCropperOpen(false);
                            }}
                          />
                        </div>
                      </div>,
                      document.body
                    )}
                  </FormGroup>
                </div>

                {/* Status */}
                {/* <div className="col-md-12 mb-3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={values.isActive}
                        onChange={(e) => setFieldValue('isActive', e.target.checked)}
                      />
                      Active Performer
                    </Label>
                  </FormGroup>
                </div> */}
              </div>
            </ModalBody>

            <ModalFooter>
              <Button color="secondary" onClick={toggle} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin me-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-save me-2"></i>
                    {lineup ? 'Update' : 'Save'} Lineup
                  </>
                )}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default LineupModal;
